import { supabase } from '@/integrations/supabase/client';
import { withRetry } from './supabaseConnection';

/**
 * Resetar status de revisão quando há alterações no dimensionamento
 * Esta função deve ser chamada sempre que houver alterações em qualquer etapa
 */
export async function resetRevisaoOnChanges(
  leadId: string, 
  dimensionamentoId: string, 
  etapa: string,
  reason?: string
): Promise<{ success: boolean; message: string; action?: string }> {
  try {
    console.log(`🔄 Resetando status de revisão devido a alterações na ${etapa}...`);
    
    // Buscar ID da proposta com retry
    const propostaData = await withRetry(async () => {
      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('id, status_revisao')
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId)
        .single();

      if (error || !data) {
        console.error('❌ Erro ao buscar proposta:', error);
        throw new Error(`Erro ao buscar proposta: ${error?.message || 'Proposta não encontrada'}`);
      }

      return data;
    });

    // Chamar função RPC para resetar status de revisão com retry
    const resetResult = await withRetry(async () => {
      const { data, error } = await supabase
        .rpc('reset_revisao_on_changes', {
          p_proposta_id: propostaData.id,
          p_reason: reason || `alteracoes_${etapa}`
        });

      if (error) {
        console.error('❌ Erro ao resetar status de revisão:', error);
        throw new Error(`Erro ao resetar status: ${error.message}`);
      }

      return data;
    });

    const result = resetResult as { success: boolean; message?: string; action?: string; previous_status?: string };
    
    if (result.success) {
      console.log('✅ Status de revisão resetado:', result);
      
      if (result.action === 'reset') {
        return {
          success: true,
          message: `Status de revisão resetado devido a alterações na ${etapa}. Status anterior: ${result.previous_status}`,
          action: 'reset'
        };
      } else {
        return {
          success: true,
          message: result.message || 'Status já estava correto',
          action: 'none'
        };
      }
    } else {
      return { 
        success: false, 
        message: result.message || 'Erro ao resetar status de revisão' 
      };
    }
    
  } catch (error) {
    console.error('❌ Erro ao resetar status de revisão:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}

/**
 * Função específica para marcar dimensionamento como alterado e resetar revisão
 */
export async function marcarDimensionamentoAlterado(
  leadId: string, 
  dimensionamentoId: string
): Promise<{ success: boolean; message: string }> {
  try {
    console.log('🔄 Marcando dimensionamento como alterado...');
    
    const data = await withRetry(async () => {
      const { data, error } = await supabase
        .rpc('marcar_dimensionamento_alterado', {
          p_lead_id: leadId,
          p_dimensionamento_id: dimensionamentoId
        });

      if (error) {
        console.error('❌ Erro ao marcar dimensionamento como alterado:', error);
        throw new Error(`Erro: ${error.message}`);
      }

      return data;
    });

    const result = data as { success: boolean; message?: string; proposta_id?: string };
    
    if (result.success) {
      console.log('✅ Dimensionamento marcado como alterado:', result);
      return {
        success: true,
        message: result.message || 'Dimensionamento marcado como alterado'
      };
    } else {
      return { 
        success: false, 
        message: result.message || 'Erro ao marcar dimensionamento como alterado' 
      };
    }
    
  } catch (error) {
    console.error('❌ Erro ao marcar dimensionamento como alterado:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Erro desconhecido' 
    };
  }
}
